import {useCallback, useEffect, useState} from 'react'

import {useSession} from '#/state/session'
import {adaptApiRoomToFrontend, type ApiRoomData, type Room} from '../types'

export function useRooms() {
  const {hasSession, currentAccount} = useSession()
  const [rooms, setRooms] = useState<Room[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Refresh rooms function with optional silent refresh (no loading state change)
  const refreshRooms = useCallback(
    async (silent = false) => {
      if (!hasSession) {
        setRooms([])
        setIsLoading(false)
        return
      }

      // Only set loading state if not a silent refresh (background polling)
      if (!silent) {
        setIsLoading(true)
      }
      setError(null)

      try {
        const response = await fetch('/api/rooms', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            error: 'Failed to fetch rooms and parse error response',
          }))
          console.error(
            'Failed to fetch rooms API error:',
            response.status,
            errorData,
          )
          throw new Error(
            errorData.error ||
              `Failed to fetch rooms. Status: ${response.status}`,
          )
        }

        const fetchedApiRooms: ApiRoomData[] = await response.json()

        // Use the conversion utility to adapt API response
        const adaptedRooms: Room[] = fetchedApiRooms.map(adaptApiRoomToFrontend)

        setRooms(adaptedRooms)
      } catch (err: any) {
        console.error('Error in refreshRooms:', err)
        setError(err.message || 'Failed to refresh rooms')
      } finally {
        setIsLoading(false)
      }
    },
    [hasSession],
  )

  // Fetch rooms on initial load
  useEffect(() => {
    if (!hasSession) {
      setRooms([])
      setIsLoading(false)
      return
    }

    refreshRooms()
  }, [hasSession, refreshRooms])

  // Set up automatic polling for real-time updates
  useEffect(() => {
    if (!hasSession) return

    // Set up polling every 30 seconds with silent refresh (no loading state)
    const interval = setInterval(() => {
      refreshRooms(true) // silent = true, won't trigger loading state
    }, 30000)

    return () => clearInterval(interval)
  }, [hasSession, refreshRooms])

  // Create a new room
  const createRoom = useCallback(
    async (
      name: string,
      flair?: string,
      isPrivate?: boolean,
    ): Promise<Room> => {
      if (!hasSession || !currentAccount) {
        throw new Error('You must be logged in to create a room')
      }

      setIsLoading(true)
      setError(null)

      try {
        // Convert single flair string to tags array for backend compatibility
        const tags = flair ? [flair] : []

        const response = await fetch('/api/rooms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            room_name: name,
            creator_username: currentAccount.handle,
            creator_did: currentAccount.did,
            tags: tags,
            ...(isPrivate && {is_private: true}),
          }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            error: 'Failed to create room and parse error response',
          }))
          console.error(
            'Failed to create room API error:',
            response.status,
            errorData,
          )
          throw new Error(
            errorData.error ||
              `Failed to create room. Status: ${response.status}`,
          )
        }

        const apiResult = await response.json()

        if (!apiResult.room || !apiResult.room.id) {
          console.error('Invalid room data from API:', apiResult)
          throw new Error('Received invalid room data from server.')
        }

        // Use the conversion utility to adapt the API response
        const newRoomForState: Room = adaptApiRoomToFrontend(apiResult.room)

        if (!isPrivate) {
          setRooms(prevRooms => [newRoomForState, ...prevRooms])
        }
        setIsLoading(false)
        return newRoomForState
      } catch (err: any) {
        console.error('Error in createRoom:', err)
        setError(
          err.message || 'An unknown error occurred while creating the room.',
        )
        setIsLoading(false)
        throw err
      }
    },
    [hasSession, currentAccount],
  )

  return {
    rooms,
    isLoading,
    error,
    createRoom,
    refreshRooms,
  }
}
